import React, { useEffect, useMemo, useRef, useState } from "react";

// Logo URLs from user assets
export const LOGO_URLS = {
  main: "/aveniq1.png",
  alt: "https://customer-assets.emergentagent.com/job_c5c5fb7d-d1cc-44e0-a158-a80c55348ee2/artifacts/9ax4sj8a_Blue%20%26%20Black%20Technology%20Logo.png"
};

// Hero background image
export const IMAGES = {
  hero_main: "https://images.unsplash.com/photo-1593720213428-28a5b9e94613?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHx3ZWIlMjBkZXZlbG9wbWVudHxlbnwwfHx8fDE3NzAzMDI0MDB8MA&ixlib=rb-4.1.0&q=85"
};

// Navigation Component with Neon Green Shadows
export function NavBar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'services', 'portfolio', 'about', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-gray-800"
         style={{ boxShadow: '0 0 20px rgba(34, 197, 94, 0.1)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <img 
              src={LOGO_URLS.main} 
              alt="Aveniq Solutions Logo" 
              className="h-8 w-auto"
              style={{ filter: 'drop-shadow(0 0 10px rgba(34, 197, 94, 0.3))' }}
            />
            <span className="text-xl font-bold text-white">Aveniq Solutions</span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {[
              { id: 'home', label: 'Home' },
              { id: 'services', label: 'Services' },
              { id: 'portfolio', label: 'Portfolio' },
              { id: 'about', label: 'About Us' },
              { id: 'contact', label: 'Contact' }
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`text-sm font-medium transition-all duration-200 ${
                  activeSection === item.id
                    ? 'text-green-400 shadow-sm'
                    : 'text-gray-300 hover:text-green-400'
                }`}
                style={{
                  textShadow: activeSection === item.id ? '0 0 10px rgba(34, 197, 94, 0.5)' : 'none'
                }}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-300 hover:text-green-400 focus:outline-none transition-colors duration-200"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-gray-900 rounded-lg mt-2 border border-gray-800"
                 style={{ boxShadow: '0 0 20px rgba(34, 197, 94, 0.2)' }}>
              {[
                { id: 'home', label: 'Home' },
                { id: 'services', label: 'Services' },
                { id: 'portfolio', label: 'Portfolio' },
                { id: 'about', label: 'About Us' },
                { id: 'contact', label: 'Contact' }
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`block w-full text-left px-3 py-2 rounded-md text-base font-medium transition-all duration-200 ${
                    activeSection === item.id
                      ? 'text-green-400 bg-gray-800 shadow-md'
                      : 'text-gray-300 hover:text-green-400 hover:bg-gray-800'
                  }`}
                  style={{
                    boxShadow: activeSection === item.id ? '0 0 10px rgba(34, 197, 94, 0.3)' : 'none'
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

// Hero Section with Neon Green Shadows
export function Hero() {
  return (
    <section id="home" className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img 
          src={IMAGES.hero_main} 
          alt="Web Development" 
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="reveal-up">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6"
              style={{ textShadow: '0 0 30px rgba(34, 197, 94, 0.3)' }}>
            Bring Your Business
            <span className="block text-green-400" style={{ textShadow: '0 0 20px rgba(34, 197, 94, 0.5)' }}>Online Today</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Professional websites, fast landing pages, maximising ROAS and stunning video/social content 
            that help businesses establish a strong digital presence they can afford.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-semibold transition-all duration-200 hover:transform hover:scale-105 border border-green-500"
              style={{ boxShadow: '0 0 20px rgba(34, 197, 94, 0.4)' }}
            >
              View Our Services
            </button>
            <button 
              onClick={() => document.getElementById('portfolio').scrollIntoView({ behavior: 'smooth' })}
              className="border border-gray-600 hover:border-green-400 text-white hover:text-green-400 px-8 py-3 rounded-lg font-semibold transition-all duration-200 hover:transform hover:scale-105 hover:shadow-lg hover:shadow-green-400/20"
            >
              See Our Work
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

// Services Section with Bento Layout
export function Services() {
  const services = [
    {
      title: "Landing Pages",
      description: "Fast, responsive landing pages to capture leads and drive conversions optimised for SEO that can accelerate your growth",
      features: ["Mobile Responsive", "Fast Loading", "SEO Optimised", "Lead Capture Forms"],
      highlight: "Perfect for marketing campaigns and lead generation",
      icon: "M13 10V3L4 14h7v7l9-11h-7z"
    },
    {
      title: "Professional Websites",
      description: "Custom websites tailored to your business needs to support long term growth that evolves with you",
      features: ["Custom Design", "CMS Integration", "E-commerce Ready", "Analytics Setup"],
      highlight: "Complete business solutions with modern functionality",
      icon: "M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    },
   {
  title: "Meta Ads Strategist",
  description: "Marketing strategies that plans, optimises and scales Facebook & Instagram ad campaigns using real-time performance insights.",
  features: [
    "Campaign Strategy & Planning",
    "Audience Research & Targeting",
    "Creative & Copy Recommendations",
    "Performance Analysis & Optimization"
  ],
  highlight: "Maximize ROAS with data-driven Meta Ad strategies & automated campaign optimization",
  icon: "M12 2a1 1 0 011 1v1h1a2 2 0 012 2v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-1v1a1 1 0 11-2 0v-1H9a2 2 0 01-2-2V9H6a1 1 0 110-2h1V5a2 2 0 012-2h1V3a1 1 0 011-1zm-1 8a1 1 0 100 2 1 1 0 000-2zm4 0a1 1 0 100 2 1 1 0 000-2z"
},
    {
      title: "Video & Content Creation",
      description: "Professional video editing and animation for engaging social media content",
      features: ["Adobe Premiere Pro", "After Effects", "CapCut", "Canva Design", "2D & 3D Animation"],
      highlight: "Bring your brand to life with stunning visual content",
      icon: "M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
    }
  ];

  return (
    <section id="services" className="py-20 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 reveal-up">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Our Services
          </h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            We offer comprehensive digital solutions to help your business thrive online
          </p>
        </div>

        {/* Bento Grid Layout with Neon Green Shadows */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
          {services.map((service, index) => (
          <div
            key={index}
            className="bg-gray-900 border border-gray-800 rounded-2xl p-8 flex flex-col h-full hover:border-green-400 transition-all duration-300 reveal-up shadow-lg hover:shadow-green-400/20"
            style={{
              boxShadow: '0 0 20px rgba(34, 197, 94, 0.1), inset 0 0 20px rgba(34, 197, 94, 0.05)'
    }}
  >
              {/* Service Header */}
              <div className="mb-6">
                <div className="w-12 h-12 bg-green-400/20 rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-green-400/30">
                  <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={service.icon} />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">{service.title}</h3>
                <p className="text-gray-300 text-lg leading-relaxed">{service.description}</p>
              </div>

              {/* Highlight */}
              <div className="mb-6 p-4 bg-green-400/10 border border-green-400/20 rounded-xl shadow-inner shadow-green-400/10">
                <p className="text-green-300 font-medium">{service.highlight}</p>
              </div>

              {/* Features List */}
              <div className="space-y-3 mt-6 pt-6 border-t border-gray-800 flex-grow">
                <h4 className="text-white font-semibold mb-3">Key Features:</h4>
                {service.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center">
                    <div className="w-2 h-2 bg-green-400 rounded-full mr-3 flex-shrink-0 shadow-sm shadow-green-400/50"></div>
                    <span className="text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-16 mb-8">
          <p className="text-green-400 font-semibold tracking-wider uppercase mb-2">
            Ready To Get Started?
          </p>

          <h3 className="text-3xl font-bold text-white mb-3">
            Let's build something great together
          </h3>

          <p className="text-gray-400 max-w-2xl mx-auto">
            Choose the service that best fits your needs and we'll help you achieve your business goals.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
          {services.map((service, index) => (
            <button
              key={index}
              onClick={() =>
                document.getElementById("contact").scrollIntoView({
                  behavior: "smooth",
                })
              }
              className="h-14 rounded-xl border border-gray-700 bg-gray-900 text-white font-medium hover:border-green-400 hover:text-green-400 transition-all duration-300 flex items-center justify-center gap-3"
            >
              <span>Get Started with {service.title}</span>
        
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          ))}
        </div>

        {/* Additional Info Section with Neon Green Shadows */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div 
            className="bg-gray-900 border border-gray-800 rounded-2xl p-8 reveal-left hover:border-green-400 transition-all duration-300"
            style={{
              boxShadow: '0 0 20px rgba(34, 197, 94, 0.1), inset 0 0 20px rgba(34, 197, 94, 0.05)'
            }}
          >
            <h3 className="text-xl font-bold text-white mb-4">Why Choose Our Services?</h3>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start">
                <span className="text-green-400 mr-2 shadow-sm shadow-green-400/50">•</span>
                <span>Affordable pricing without compromising quality</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-400 mr-2 shadow-sm shadow-green-400/50">•</span>
                <span>Fast delivery and responsive communication</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-400 mr-2 shadow-sm shadow-green-400/50">•</span>
                <span>Modern, professional designs that convert</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-400 mr-2 shadow-sm shadow-green-400/50">•</span>
                <span>Ongoing support and maintenance included</span>
              </li>
            </ul>
          </div>
          
          <div 
            className="bg-gray-900 border border-gray-800 rounded-2xl p-8 reveal-up hover:border-green-400 transition-all duration-300"
            style={{
              boxShadow: '0 0 20px rgba(34, 197, 94, 0.1), inset 0 0 20px rgba(34, 197, 94, 0.05)'
            }}
          >
            <h3 className="text-xl font-bold text-white mb-4">Our Process</h3>
            <div className="space-y-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-4 shadow-lg shadow-green-400/30">1</div>
                <span className="text-gray-300">Consultation & Planning</span>
              </div>
              <div className="flex items-center">
                <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-4 shadow-lg shadow-green-400/30">2</div>
                <span className="text-gray-300">Design & Development</span>
              </div>
              <div className="flex items-center">
                <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-4 shadow-lg shadow-green-400/30">3</div>
                <span className="text-gray-300">Testing & Launch</span>
              </div>
              <div className="flex items-center">
                <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-4 shadow-lg shadow-green-400/30">4</div>
                <span className="text-gray-300">Support & Maintenance</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Portfolio Section with Fixed Sections (No Filters)
export function Portfolio() {
  const [selectedItem, setSelectedItem] = useState(null);

  // Content Creation Projects (2 videos + 2 images)
  const contentCreationProjects = [
    {
      id: 1,
      type: 'website',
      title: 'Professional Reels & Videos',
      description: 'Dynamic promotional video with motion graphics',
      websiteUrl: 'https://www.behance.net/gallery/244551225/Video-Editing-3D-and-2D-MotionAnimation',
      imageUrl: '/vidthumb.png',
      tools: ['Adobe Premiere Pro', 'After Effects', 'CapCut Pro']
    },
    {
      id: 2,
      type: 'website',
      title: 'Social Media Content',
      description: '3D product showcase with smooth animations',
      websiteUrl: 'https://www.behance.net/gallery/244554625/Social-Media-Content-Creation',
      imageUrl: '/imgthumb.png',
      tools: ['Photoshop', 'Canva', 'Lightroom']
    },
    {
      id: 3,
      type: 'website',
      title: 'Cinematic Real Estate Walkthrough Videos',
      description: 'Cinematic Real Estate Walkthroughs with smooth animations',
      websiteUrl: 'https://www.behance.net/gallery/252770193/Real-Estate-Cinematic-AI-Walkthrough-Videos',
      imageUrl: '/video.png',
      tools: ['Canva', 'Photos', 'Claude']
    }
  ];

  // Website Projects (3 websites)
  const websiteProjects = [
    {
      id: 5,
      type: 'website',
      title: ' MAISON ÉLISE',
      description: 'Premium bakery website focused on luxury pastries and refined desserts',
      websiteUrl: 'https://luxury-patisserie-site.vercel.app/',
      imageUrl: '/Maison.png',
      tech: ['React', 'JSX', 'Python', 'Tailwind CSS' ]
    },
    {
      id: 6,
      type: 'website',
      title: 'E-commerce Shopify Store',
      description: 'Premium eastern wear brand focused on quality, elegance and sophistication',
      websiteUrl: 'https://www.pahnava.pk/',
      imageUrl: './pahnava.png',
      tech: ['Shopify', 'HTML', 'CSS']
    },
     {
      id: 7,
      type: 'website',
      title: 'Corporate Website',
      description: 'AI & software company building scalable systems with integrated CMS solutions',
      websiteUrl: 'https://nexora-systems-nu.vercel.app/',
      imageUrl: './nexora.png',
      tech: ['React', 'Craco', 'Python', 'MongoDB']
    },
    {
      id: 8,
      type: 'website',
      title: 'Artistic Portofolio Website',
      description: 'Creative art portfolio showcasing original artwork & visual storytelling',
      websiteUrl: 'https://hum-aur-zindagi.vercel.app/',
      imageUrl: '/webthumb.png',
      tech: ['React', 'Radix', 'Python', 'Tailwind CSS']
    },
    {
      id: 9,
      type: 'website',
      title: 'Social Media Agency',
      description: 'Portfolio website for social media management, video production & digital marketing',
      websiteUrl: 'https://socialmedia-manager-portfolio.vercel.app/',
      imageUrl: '/social media portfolio.png',
      tech: ['React', 'Framer Motion', 'Python', 'Tailwind CSS']
    },
    {
      id: 10,
      type: 'website',
      title: 'Obsidian Gains Gym',
      description: 'High-energy fitness website designed for gyms & fitness brands',
      websiteUrl: 'https://obsidian-gains-gym.vercel.app/',
      imageUrl: '/Obsidian_Gym.png',
      tech: ['React', 'Python', 'Tailwind CSS']
    },
    {
      id: 11,
      type: 'website',
      title: 'Atelier Vanta',
      description: 'Elegant architecture website featuring projects, concepts & creative vision',
      websiteUrl: 'https://architect-site-kohl.vercel.app/',
      imageUrl: '/Architect_site.png',
      tech: ['React', 'JSX', 'Python' ]
    },
    {
      id: 12,
      type: 'website',
      title: ' The Arisan Brew',
      description: 'Modern café website with elegant design, smooth animations, and menu showcase',
      websiteUrl: 'https://cafe-website-theta-five.vercel.app/',
      imageUrl: '/artisan-brew.png',
      tech: ['React', 'JSX', 'Python', 'Lucide' ]
    },
    {
      id: 13,
      type: 'website',
      title: ' FlowFix Plumbing',
      description: 'Modern plumbing website showcasing services, repairs, and emergency solutions',
      websiteUrl: 'https://plumbing-site-opal.vercel.app/',
      imageUrl: '/flowfix.png',
      tech: ['React', 'JSX', 'Python' ]
    },
    {
      id: 14,
      type: 'website',
      title: ' Freelancer Portfolio',
      description: 'Modern portfolio with smooth animations and responsive design',
      websiteUrl: 'https://portfoliotech-gdy98bc28-aveniqsolutions-7442s-projects.vercel.app/',
      imageUrl: '/porfolio.png',
      tech: ['React', 'JSX', 'Python' ]
    },
    {
      id: 15,
      type: 'website',
      title: 'Aurum Jewellery Site',
      description: 'Luxury jewelry brand website featuring refined collections and visuals',
      websiteUrl: 'https://aurum-jewelry-site-rc2dx09ue-aveniqsolutions-7442s-projects.vercel.app/',
      imageUrl: '/aurum.png',
      tech: ['React', 'CRACO', 'Python' ]
    },
    {
      id: 16,
      type: 'website',
      title: 'Radiyah UK Ebook Store',
      description: 'Luxury jewelry brand website featuring refined collections and visuals',
      websiteUrl: 'https://radiyahuk.vercel.app/',
      imageUrl: '/radiyahuk.png',
      tech: ['React', 'JSX', 'Python' ]
    },
  ];
  
  // AI Chatbot Projects (3 chatbots)
  // const aiChatbotProjects = [
  //   {
  //     id: 8,
  //     type: 'chatbot',
  //     title: 'Customer Support Bot',
  //     description: 'AI-powered customer support chatbot with natural language processing',
  //     demoUrl: 'https://example-chatbot-demo.com', // Replace with your actual demo
  //     imageUrl: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?w=800',
  //     tech: ['OpenAI GPT', 'Python', 'FastAPI', 'React']
  //   },
  //   {
  //     id: 9,
  //     type: 'chatbot',
  //     title: 'Sales Assistant Bot',
  //     description: 'Intelligent sales assistant to help customers find products',
  //     demoUrl: 'https://example-sales-bot.com', // Replace with your actual demo
  //     imageUrl: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=800',
  //     tech: ['Claude AI', 'Node.js', 'MongoDB', 'Socket.io']
  //   },
  //   {
  //     id: 10,
  //     type: 'chatbot',
  //     title: 'Educational Tutor Bot',
  //     description: 'AI tutor bot for personalized learning experiences',
  //     demoUrl: 'https://example-tutor-bot.com', // Replace with your actual demo
  //     imageUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800',
  //     tech: ['Gemini AI', 'Python', 'Django', 'WebSocket']
  //   }
  // ];

  const handleItemClick = (item) => {
    if (item.type === 'website') {
      window.open(item.websiteUrl, '_blank');
    // } else if (item.type === 'chatbot') {
    //   window.open(item.demoUrl, '_blank');
    } else {
      setSelectedItem(item);
    }
  };

  const closeModal = () => {
    setSelectedItem(null);
  };

  const renderProjectGrid = (projects, sectionTitle) => (
    <div className="mb-20">
      <h3 className="text-2xl font-bold text-white mb-8 text-center"
          style={{ textShadow: '0 0 15px rgba(34, 197, 94, 0.3)' }}>
        {sectionTitle}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {projects.map((item) => (
          <div 
            key={item.id} 
            className="group cursor-pointer reveal-up bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden hover:border-green-400 transition-all duration-300"
            onClick={() => handleItemClick(item)}
            style={{
              boxShadow: '0 0 20px rgba(34, 197, 94, 0.1), inset 0 0 20px rgba(34, 197, 94, 0.05)'
            }}
          >
            <div className="relative overflow-hidden">
              <img 
                src={item.type === 'video' ? item.thumbnail : item.imageUrl} 
                alt={item.title}
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
              />
                  
          {/* Type Indicator */}
              <div className="absolute top-4 left-4">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  item.type === 'video' ? 'bg-red-600/80 text-white' :
                  item.type === 'website' ? 'bg-blue-600/80 text-white' :
                  item.type === 'chatbot' ? 'bg-green-600/80 text-white' :
                  'bg-purple-600/80 text-white'
                }`}>
                  {item.type === 'video' ? '▶ Video' : 
                   item.type === 'website' ? '🌐 Website' : 
                   item.type === 'chatbot' ? '🤖 AI Bot' :
                   '🎨 Design'}
                </span>
              </div>

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center justify-between">
                    <span className="text-green-400 text-sm font-medium">
                      {item.type === 'website' ? 'Click to Visit' : 
                       item.type === 'chatbot' ? 'Click to Demo' : 
                       'Click to View'}
                    </span>
                    <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <h4 className="text-xl font-bold text-white mb-2">{item.title}</h4>
              <p className="text-gray-300 text-sm mb-4">{item.description}</p>
              <div className="flex flex-wrap gap-2">
                {(item.tools || item.tech)?.map((tool, idx) => (
                  <span key={idx} className="bg-green-600/20 text-green-400 px-2 py-1 rounded text-xs border border-green-400/20">
                    {tool}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <section id="portfolio" className="py-20 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 reveal-up">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4"
              style={{ textShadow: '0 0 20px rgba(34, 197, 94, 0.3)' }}>
            Our Portfolio
          </h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Showcasing our latest projects across content creation, web development and AI solutions
          </p>
        </div>

        {/* Content Creation Section */}
        {renderProjectGrid(contentCreationProjects, "Content Creation")}

        {/* Websites Section */}
        {renderProjectGrid(websiteProjects, "Websites")}

        {/* AI Chatbots Section */}


        {/* Modal for Videos and Images */}
        {selectedItem && (
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" onClick={closeModal}>
            <div className="max-w-4xl w-full bg-gray-900 rounded-2xl overflow-hidden border border-green-400/30" 
                 style={{ boxShadow: '0 0 40px rgba(34, 197, 94, 0.3)' }}
                 onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-2xl font-bold text-white">{selectedItem.title}</h3>
                  <button 
                    onClick={closeModal}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                {selectedItem.type === 'video' ? (
                  <video 
                    controls 
                    className="w-full rounded-lg"
                    poster={selectedItem.thumbnail}
                  >
                    <source src={selectedItem.videoUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <img 
                    src={selectedItem.imageUrl} 
                    alt={selectedItem.title}
                    className="w-full rounded-lg"
                  />
                )}
                
                <div className="mt-4">
                  <p className="text-gray-300 mb-4">{selectedItem.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedItem.tools?.map((tool, idx) => (
                      <span key={idx} className="bg-green-600/20 text-green-400 px-3 py-1 rounded-full text-sm border border-green-400/20">
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// About Section with Neon Green Shadows
export function About() {
  return (
    <section id="about" className="py-20 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="reveal-left">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6"
                style={{ textShadow: '0 0 20px rgba(34, 197, 94, 0.3)' }}>
              About Aveniq Solutions
            </h2>
            <p className="text-lg text-gray-300 mb-6">
              We specialise in helping businesses, startups and individuals establish 
              a strong online presence with professional, modern & affordable digital solutions.
            </p>
            <p className="text-gray-300 mb-6">
              Our mission is to make high-quality web development & content creation accessible to everyone. 
              We believe that every business deserves a professional digital presence that 
              communicates trust & professionalism.
            </p>
            <div className="grid grid-cols-2 gap-6">
              <div 
                className="bg-gray-900 border border-gray-800 rounded-xl p-6 text-center hover:border-green-400 transition-all duration-300"
                style={{ boxShadow: '0 0 20px rgba(34, 197, 94, 0.1)' }}
              >
                <h3 className="text-3xl font-bold text-green-400 mb-2" 
                    style={{ textShadow: '0 0 15px rgba(34, 197, 94, 0.5)' }}>50+</h3>
                <p className="text-gray-300">Projects Completed</p>
              </div>
              <div 
                className="bg-gray-900 border border-gray-800 rounded-xl p-6 text-center hover:border-green-400 transition-all duration-300"
                style={{ boxShadow: '0 0 20px rgba(34, 197, 94, 0.1)' }}
              >
                <h3 className="text-3xl font-bold text-green-400 mb-2"
                    style={{ textShadow: '0 0 15px rgba(34, 197, 94, 0.5)' }}>100%</h3>
                <p className="text-gray-300">Client Satisfaction</p>
              </div>
            </div>
          </div>
          <div className="reveal-up">
            <div 
              className="bg-gray-900 border border-gray-800 rounded-2xl p-8 hover:border-green-400 transition-all duration-300"
              style={{ boxShadow: '0 0 20px rgba(34, 197, 94, 0.1)' }}
            >
              <h3 className="text-2xl font-bold text-white mb-6">Our Values</h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div 
                    className="w-12 h-12 bg-green-600/20 rounded-xl flex items-center justify-center mr-4 flex-shrink-0 border border-green-400/20"
                    style={{ boxShadow: '0 0 15px rgba(34, 197, 94, 0.3)' }}
                  >
                    <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">Quality First</h4>
                    <p className="text-gray-300 text-sm">Every project is built with attention to detail and modern best practices.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div 
                    className="w-12 h-12 bg-green-600/20 rounded-xl flex items-center justify-center mr-4 flex-shrink-0 border border-green-400/20"
                    style={{ boxShadow: '0 0 15px rgba(34, 197, 94, 0.3)' }}
                  >
                    <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">Fast Delivery</h4>
                    <p className="text-gray-300 text-sm">Quick turnaround times without compromising on quality.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div 
                    className="w-12 h-12 bg-green-600/20 rounded-xl flex items-center justify-center mr-4 flex-shrink-0 border border-green-400/20"
                    style={{ boxShadow: '0 0 15px rgba(34, 197, 94, 0.3)' }}
                  >
                    <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">Ongoing Support</h4>
                    <p className="text-gray-300 text-sm">Continuous support and maintenance for your digital presence.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Contact Form Component with Formspree Integration
export function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mzdjdzkl';
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    return newErrors;
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: ''
      }));
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      setStatus('');
      return;
    }
    
    setIsLoading(true);
    setStatus('');
    
    try {
      
     const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
       body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          _replyto: formData.email, // Formspree will use this as reply-to
          _subject: `New Contact from Aveniq Solutions: ${formData.subject}` // Custom subject line
        })
      });
      
      if (response.ok) {
        setStatus('Message sent successfully! We will contact you soon.');
        setFormData({ name: '', email: '', subject: '', message: '' });
        setErrors({});
      } else {
        const errorData = await response.json();
        setStatus('Error: ' + (errorData.error || 'Failed to send message. Please try again.'));
      }
    } catch (error) {
      setStatus('Error: Unable to send message. Please check your internet connection and try again.');
      console.error('Contact form error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="bg-black rounded-xl py-12 px-8 border border-gray-800 hover:border-green-400 transition-all duration-300"
      style={{ boxShadow: '0 0 20px rgba(34, 197, 94, 0.1)' }}
    >
      <h3 className="text-2xl font-bold text-white mb-6"
          style={{ textShadow: '0 0 15px rgba(34, 197, 94, 0.3)' }}>Send us a Message</h3>
            
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
              Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-400 transition-all duration-200 ${
                errors.name ? 'border-red-500' : 'border-gray-600 hover:border-green-400/50'
              }`}
              placeholder="Your full name"
              style={{
                boxShadow: errors.name ? 'none' : '0 0 10px rgba(34, 197, 94, 0.1)'
              }}
            />
            {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name}</p>}
          </div>
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
              Email *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-400 transition-all duration-200 ${
                errors.email ? 'border-red-500' : 'border-gray-600 hover:border-green-400/50'
              }`}
              placeholder="your.email@example.com"
              style={{
                boxShadow: errors.email ? 'none' : '0 0 10px rgba(34, 197, 94, 0.1)'
              }}
            />
            {errors.email && <p className="text-red-400 text-sm mt-1">{errors.email}</p>}
          </div>
        </div>
        
        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">
            Subject *
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-400 transition-all duration-200 ${
              errors.subject ? 'border-red-500' : 'border-gray-600 hover:border-green-400/50'
            }`}
            placeholder="What is this about?"
            style={{
              boxShadow: errors.subject ? 'none' : '0 0 10px rgba(34, 197, 94, 0.1)'
            }}
          />
          {errors.subject && <p className="text-red-400 text-sm mt-1">{errors.subject}</p>}
        </div>
        
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
            Message *
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows="5"
            className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-400 resize-none transition-all duration-200 ${
              errors.message ? 'border-red-500' : 'border-gray-600 hover:border-green-400/50'
            }`}
            placeholder="Tell us about your project..."
            style={{
              boxShadow: errors.message ? 'none' : '0 0 10px rgba(34, 197, 94, 0.1)'
            }}
          ></textarea>
          {errors.message && <p className="text-red-400 text-sm mt-1">{errors.message}</p>}
        </div>
        
        <button 
          type="submit" 
          disabled={isLoading}
          className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-800 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 hover:transform hover:scale-105 border border-green-500"
          style={{ boxShadow: '0 0 20px rgba(34, 197, 94, 0.4)' }}
        >
          {isLoading ? 'Sending...' : 'Send Message'}
        </button>
      </form>
      
      {status && (
        <div className={`mt-4 p-4 rounded-lg border ${
          status.includes('Error') 
            ? 'bg-red-900/50 text-red-300 border-red-800' 
            : 'bg-green-900/50 text-green-300 border-green-800'
        }`}
             style={{
               boxShadow: status.includes('Error') 
                 ? '0 0 15px rgba(239, 68, 68, 0.3)' 
                 : '0 0 15px rgba(34, 197, 94, 0.3)'
             }}>
          {status}
        </div>
      )}
    </div>
  );
}



export function Contact() {
  return (
    <section id="contact" className="py-20 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="text-center mb-16 reveal-up">
          <h2
            className="text-3xl sm:text-4xl font-bold text-white mb-4"
            style={{ textShadow: '0 0 20px rgba(34, 197, 94, 0.3)' }}
          >
            Get In Touch
          </h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Ready to bring your business online? Let's discuss your project and
            create something amazing together.
          </p>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column */}
          <div className="space-y-8">
            {/* Contact Info */}
            <div
              className="bg-black border border-gray-800 rounded-2xl p-8 hover:border-green-400 transition-all duration-300 reveal-left"
              style={{ boxShadow: '0 0 20px rgba(34, 197, 94, 0.1)' }}
            >
              <h3 className="text-xl font-bold text-white mb-6">Contact Information</h3>
              <div className="space-y-6">
                {/* Email */}
                <div className="flex items-center">
                  <div
                    className="w-12 h-12 bg-green-600/20 rounded-xl flex items-center justify-center mr-4 border border-green-400/20"
                    style={{ boxShadow: '0 0 15px rgba(34, 197, 94, 0.3)' }}
                  >
                    <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <a href="mailto:contact@aveniq-solutions.com" className="text-gray-300 hover:text-green-400 transition-colors">
                    contact@aveniq-solutions.com
                  </a>
                </div>

                {/* Phone */}
                <div className="flex items-center">
                  <div
                    className="w-12 h-12 bg-green-600/20 rounded-xl flex items-center justify-center mr-4 border border-green-400/20"
                    style={{ boxShadow: '0 0 15px rgba(34, 197, 94, 0.3)' }}
                  >
                    <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3l2 5-2 2a16 16 0 006 6l2-2 5 2v3a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                  </div>
                  <a href="tel:+32465973568" className="text-gray-300 hover:text-green-400 transition-colors">
                    +32 (465) 973-568
                  </a>
                </div>

                {/* Response Time */}
                <div className="flex items-center">
                  <div
                    className="w-12 h-12 bg-green-600/20 rounded-xl flex items-center justify-center mr-4 border border-green-400/20"
                    style={{ boxShadow: '0 0 15px rgba(34, 197, 94, 0.3)' }}
                  >
                    <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <span className="text-gray-300">Response within 24 hours</span>
                </div>
              </div>
            </div>

            {/* Why Choose Us */}
            <div
              className="bg-black border border-gray-800 rounded-2xl p-8 hover:border-green-400 transition-all duration-300 reveal-left"
              style={{ boxShadow: '0 0 20px rgba(34, 197, 94, 0.1)' }}
            >
              <h3 className="text-xl font-bold text-white mb-6">Why Choose Us?</h3>
              <ul className="space-y-4">
                {[
                  'Affordable pricing for quality work',
                  'Modern, responsive designs',
                  'Ongoing support and maintenance',
                  'SEO-optimised solutions',
                ].map((item, index) => (
                  <li key={index} className="flex items-center text-gray-300">
                    <div
                      className="w-2 h-2 bg-green-400 rounded-full mr-3 flex-shrink-0"
                      style={{ boxShadow: '0 0 8px rgba(34, 197, 94, 0.5)' }}
                    ></div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right Column - Contact Form */}
          <div className="reveal-up">
            <ContactForm />
          </div>
        </div>
      </div>
    </section>
  );
}

// Footer with Neon Green Shadows
export function Footer() {
  return (
    <footer className="bg-gray-900 border-t border-gray-800 py-12"
            style={{ boxShadow: '0 -5px 20px rgba(34, 197, 94, 0.1)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <img 
                src={LOGO_URLS.main} 
                alt="Aveniq Solutions Logo" 
                className="h-8 w-auto"
                style={{ filter: 'drop-shadow(0 0 10px rgba(34, 197, 94, 0.3))' }}
              />
              <span className="text-xl font-bold text-white">Aveniq Solutions</span>
            </div>
            <p className="text-gray-400">
              Professional web development, marketing & content creation services to help your business thrive online.
            </p>
          </div>

          
          <div>
            <h3 className="text-white font-semibold mb-4"
                style={{ textShadow: '0 0 10px rgba(34, 197, 94, 0.3)' }}>Services</h3>
            <ul className="space-y-2 text-gray-400">
              <li className="hover:text-green-400 transition-colors duration-200">Landing Pages</li>
              <li className="hover:text-green-400 transition-colors duration-200">Professional Websites</li>
              <li className="hover:text-green-400 transition-colors duration-200">Marketing</li>
              <li className="hover:text-green-400 transition-colors duration-200">Video & Content Creation</li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-white font-semibold mb-4"
                style={{ textShadow: '0 0 10px rgba(34, 197, 94, 0.3)' }}>Contact</h3>
            <ul className="space-y-2 text-gray-400">
              <li className="hover:text-green-400 transition-colors duration-200"><a href="mailto:contact@aveniq-solutions.com" className="text-gray-300 hover:text-green-400 transition-colors">
                contact@aveniq-solutions.com</a></li>
              <li className="hover:text-green-400 transition-colors duration-200">Professional Support</li>
              <li className="hover:text-green-400 transition-colors duration-200">Quick Response Time</li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-gray-800 mt-8 pt-8 text-center">
          <p className="text-gray-400">
            © 2026 Aveniq Solutions. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
